import React, { useState, useEffect, useContext } from 'react';
import { StorageContext } from '../../contexts/StorageContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import PromptPanel from '../PromptPanel';
import ResponsePanel from '../ResponsePanel';
import PromptGenerator from '../../services/PromptGenerator';
import ResponseProcessor from '../../services/ResponseProcessor';
import './DesignPhase.css';

/**
 * Design phase component
 * Handles creating technical specifications and file organization
 */
function DesignPhase() {
  const { storage } = useContext(StorageContext);
  const { project, updateProject } = useContext(ProjectContext);
  const [components, setComponents] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);
  const [promptGenerator] = useState(new PromptGenerator());
  const [responseProcessor] = useState(new ResponseProcessor(storage));

  // Load components
  useEffect(() => {
    const loadComponents = async () => {
      if (!project) return;
      
      try {
        const projectComponents = await storage.getComponentsByProject(project.id);
        setComponents(projectComponents);
      } catch (error) {
        console.error('Error loading components:', error);
      }
    };
    
    loadComponents();
  }, [project, storage]);

  // Generate design prompt
  const handleGeneratePrompt = async () => {
    if (components.length === 0) {
      alert('No components found. Please complete the Planning phase first.');
      return;
    }
    
    try {
      setIsGeneratingPrompt(true);
      const generatedPrompt = await promptGenerator.generateDesignPrompt(components, project.id);
      setPrompt(generatedPrompt);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Process AI response
  const handleProcessResponse = async (response) => {
    try {
      setIsProcessingResponse(true);
      
      const result = await responseProcessor.processResponse(
        response,
        'DESIGN',
        project.id
      );
      
      if (result.success) {
        // Update project with phase completion
        const updatedProject = {
          ...project,
          designComplete: true,
          updated: Date.now()
        };
        
        await storage.updateProject(updatedProject);
        updateProject(updatedProject);
        
        // Store processing results
        setProcessingResults(result);
        
        return { success: true };
      } else {
        return { 
          success: false,
          error: result.error || 'Failed to process response'
        };
      }
    } catch (error) {
      console.error('Error processing response:', error);
      return { 
        success: false,
        error: 'Error processing response: ' + error.message 
      };
    } finally {
      setIsProcessingResponse(false);
    }
  };

  // Component summary display
  const renderComponentSummary = () => {
    if (components.length === 0) {
      return (
        <div className="no-components-message">
          No components found. Please complete the Planning phase first.
        </div>
      );
    }

    return (
      <div className="component-summary">
        {components.map(component => (
          <div key={component.id} className="component-card">
            <h4 className="component-name">{component.name}</h4>
            <p className="component-description">{component.description}</p>
            {component.dependencies && component.dependencies.length > 0 && (
              <div className="component-dependencies">
                <span className="dependencies-label">Dependencies:</span>
                <div className="dependencies-list">
                  {component.dependencies.map(dep => {
                    const depComponent = components.find(c => c.id === dep);
                    return (
                      <span key={dep} className="dependency-item">
                        {depComponent ? depComponent.name : dep}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="design-phase">
      <div className="phase-description">
        <h2>Design Phase</h2>
        <p>
          In this phase, you'll create detailed technical specifications and organize
          files into implementation batches based on dependencies.
        </p>
      </div>
      
      <div className="components-section">
        <h3>Project Components</h3>
        <p className="instructions">
          These components were identified during the Planning phase and will be used to
          create detailed technical specifications.
        </p>
        {renderComponentSummary()}
      </div>
      
      {processingResults && (
        <div className="design-results">
          <h3>Design Results</h3>
          <div className="results-summary">
            <p>Successfully processed {processingResults.files || 0} file specifications and created {processingResults.batches || 0} implementation batches.</p>
          </div>
        </div>
      )}
      
      <div className="ai-interaction">
        <PromptPanel 
          prompt={prompt}
          onGeneratePrompt={handleGeneratePrompt}
          isLoading={isGeneratingPrompt}
          title="Design Prompt"
        />
        
        <ResponsePanel 
          onProcessResponse={handleProcessResponse}
          isProcessing={isProcessingResponse}
          processingResults={processingResults}
          title="Design Response"
        />
      </div>
    </div>
  );
}

export default DesignPhase;
